import { ref, computed } from 'vue'
import axios from 'axios'

/**
 * Composable para gerenciar logs e estatísticas de notificações
 */
export function useNotificationLogs() {
  const logs = ref([])
  const stats = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const paginacao = ref({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  })

  // Status possíveis e suas cores
  const statusConfig = {
    pendente: { label: 'Pendente', cor: '#6c757d', icon: 'hourglass_empty' },
    enviado: { label: 'Enviado', cor: '#0d6efd', icon: 'send' },
    entregue: { label: 'Entregue', cor: '#198754', icon: 'check_circle' },
    falha: { label: 'Falha', cor: '#dc3545', icon: 'error' },
  }

  /**
   * Carregar logs paginados
   */
  async function carregarLogs(filtros = {}, pagina = 1) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      params.append('page', pagina)
      if (filtros.canal) params.append('canal', filtros.canal)
      if (filtros.status) params.append('status', filtros.status)
      if (filtros.campaign_id) params.append('campaign_id', filtros.campaign_id)
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio)
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim)
      if (filtros.per_page) params.append('per_page', filtros.per_page)

      const response = await axios.get(`/api/notificacoes/logs?${params}`)
      logs.value = response.data.data
      paginacao.value = {
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
      }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao carregar logs'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar estatísticas
   */
  async function carregarStats() {
    loading.value = true
    try {
      const response = await axios.get('/api/notificacoes/logs/stats')
      stats.value = response.data
      return stats.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao carregar estatísticas'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar logs de um paciente específico
   */
  async function carregarLogsPaciente(pacienteId) {
    loading.value = true
    try {
      const response = await axios.get(`/api/notificacoes/logs/paciente/${pacienteId}`)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  // Computed
  const totalEnviados = computed(() => stats.value?.total_enviados || 0)
  const totalFalhas = computed(() => stats.value?.total_falhas || 0)
  const taxaSucesso = computed(() => {
    if (!stats.value) return 0
    const total = stats.value.total_enviados + stats.value.total_falhas
    if (total === 0) return 0
    return Math.round((stats.value.total_enviados / total) * 100)
  })

  return {
    // State
    logs,
    stats,
    loading,
    error,
    paginacao,
    statusConfig,

    // Computed
    totalEnviados,
    totalFalhas,
    taxaSucesso,

    // Actions
    carregarLogs,
    carregarStats,
    carregarLogsPaciente,
  }
}
